export interface ScriptType {
  id?: string;
  title: string;
  body: string;
  source: 'HUMAN' | 'CHATGPT';
  organizationId: string;
  updatedAt: string;
}

export interface ContactListDto {
  id: string;
  organizationId: string;
  title: string;
  sequenceId?: number;
  type?: 'CSV' | 'OUTREACH' | 'HUBSPOT' | 'SALESFORCE' | 'SESSIONS';
  contactsCount?: number;
  warningsCount?: number;
}

export interface VoicemailDto {
  id?: string;
  _id?: string;
  title: string;
  recordingUrl?: string;
  url?: string;
  durationSec?: number;
  duration?: number | string;
  recordingDuration?: number | string;
  duration_seconds?: number | string;
  length?: number | string;
  status?: 'active' | 'draft' | 'default';
  createdAt?: string;
  updatedAt?: string;
}

export interface ContactPreview {
  id: string;
  name: string;
  phone: string;
  accountName?: string;
  jobTitle?: string;
  email?: string;
  notes?: string;
}

export interface CreateCallSessionDto {
  sdrId: string;
  sdrStatus: 'free' | 'busy' | 'on_call';
  startAt: string;
  contactList: Array<{ contactListId: string; completed: boolean }>;
  voiceDropsIds: string[];
  scriptId: string;
  scriptTitle: string;
  speech: string;
  agents: string[];
  status: 'started';
  random: boolean;
  action?: { action: 'started' | 'paused' | 'finished' | 'transferred'; userId: string };
}

export interface CallSessionResponse {
  id: string;
  sdrId: string;
  status: 'started' | 'paused' | 'finished';
  scriptId?: string;
  scriptTitle?: string;
  voicemailUrl?: string;
  voicemailTitle?: string;
  speech?: string;
  contactList?: Array<{ contactListId: string; completed: boolean; title?: string }>;
  contactListTitle?: string;
  voiceDropsIds?: string[];
  agents?: string[];
  queueLength?: number;
  totalContacts?: number;
  random?: boolean;
  ongoingCallAction?: OngoingCallAction;
}

export interface CallLogEntry {
  id: string;
  callId: string;
  contactId?: string;
  contactName?: string;
  accountName?: string;
  jobTitle?: string;
  phone?: string;
  status?: string;
  disposition?: string | null;
  notesAgent?: string | null;
  createdAt?: string;
}

export interface CallLogSDR {
  id: string;
  callId: string;
  callSessionId: string;
  sdrId: string;
  contactListId: string;
  notesAgent?: string;
  notesSDR?: string;
  contact:  callLogContact
}

export interface contactMeta {
  externalId: string;
}

export interface callLogContact {
  id: string;
  accountName?: string
  contactListId: string
  jobTitle?: string
  name: string
  notes: string
  phone: string
  type?: 'CSV' | 'OUTREACH' | 'HUBSPOT' | 'SALESFORCE' | 'SESSIONS';
  email: string;
  meta: contactMeta;
}

export interface ContactInterface {
  accountName: string;
  contactListId: string;
  email: string;
  id: string;
  jobTitle: string;
  name: string;
  phone: string;
  crmId: string;
  notes: string;
  notesAgent: string;
  notesSDR: string;
  errorMessages: string[];
}

export interface DialingContact {
  id: string;
  name: string;
  phone: string;
  accountName?: string;
  jobTitle?: string;
  email?: string;
  notes?: string;
  notesContact?: string;
  notesSdr?: string;
  status?: 'pending' | 'called' | 'dialing' | 'answered' | 'voicemail' | 'transferred' | 'failed' | 'cut_off';
  callId?: string;
  notesAgent?: string;
  finalStatus?: string;
}

/**
 * Response from getDialingStatus endpoint - matches v1 structure
 * Returns condenseInfo with action field (called, transferred, answered, etc.)
 */
export type CondenseInfoAction =
  | 'called'
  | 'transferred'
  | 'answered'
  | 'voicemail'
  | 'not_answered'
  | 'transfer_error'
  | 'cut_off'
  | 'call_ended';

export interface CondenseInfoItem {
  id?: string;
  _id?: string;
  action: CondenseInfoAction;
  disposition?: string;
  agentId?: string;
  timestamp?: string;
  contactId: string;
  jobTitle?: string;
  email?: string;
  accountName?: string;
  notes?: string;
  notesAgent?: string;
  notesSDR?: string;
  contactListId?: string;
  name: string;
  phone: string;
}

export interface GetDialingStatusResponse {
  condenseInfo: CondenseInfoItem[];
  queueLength: number;
}

export type WizardStep = 1 | 2 | 3 | 4 | 5;

export interface WizardStepConfig {
  step: WizardStep;
  title: string;
  description: string;
}

export const WIZARD_STEPS: WizardStepConfig[] = [
  { step: 1, title: 'Select Intent', description: 'Choose an intent for your calls' },
  { step: 2, title: 'Select Contacts', description: 'Choose a contact list' },
  { step: 3, title: 'Select Voicemail', description: 'Choose a voicemail recording' },
  { step: 4, title: 'Session Settings', description: 'Configure session behavior' },
  { step: 5, title: 'Summary', description: 'Review and create session' },
];

export enum OngoingCallAction {
  CUT_OFF = 'CUT_OFF',
  HOLD = 'HOLD',
}

export enum CallSessionStatus {
  STARTED = 'started',
  FINISHED = 'finished',
  PAUSED = 'paused',
}

export interface CallSessionConfig {
  id: string;
  callSessionId: string;
  ongoingCallAction: OngoingCallAction;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateCallSessionConfigDto {
  callSessionId: string;
  ongoingCallAction: OngoingCallAction;
}

export interface UpdateCallSessionConfigDto {
  ongoingCallAction: OngoingCallAction;
}

export interface CallSessionConfigResponse {
  success: boolean;
  data?: CallSessionConfig;
  error?: {
    message: string;
    status?: number;
  };
}

/** Sessions tab — re-use existing v1/v2 endpoints in contacts-nest
 *
 * `GET /call-sessions/recycle-contacts/:sdrId/:orgId` returns a flat list of
 * { contactListId, callSessionId, listTitle } from the SDR's past sessions.
 */

export interface SdrCallSessionSummary {
  contactListId: string;
  callSessionId: string;
  listTitle?: string;
  /** Sent by the backend once the entity is extended to include CallSession.createdAt. */
  createdAt?: string;
}

export type SessionContactStatus =
  | 'noAnswer'
  | 'cutOff'
  | 'transferError'
  | 'voicemail'
  | 'fuegoLead';

export const SESSION_CONTACT_STATUSES: SessionContactStatus[] = [
  'noAnswer',
  'cutOff',
  'transferError',
  'voicemail',
  'fuegoLead',
];

/** Mirrors contacts-nest ContactsFilterDto (POST /contacts/v2/recycle/call-session-ids) */
export interface SessionContactsFilterDto {
  callSessionIds: string[];
  flagged: boolean;
  no_answer: boolean;
  voicemail: boolean;
  cut_off: boolean;
  transfer_error: boolean;
}

export interface SessionContactItem {
  id: string;
  name: string;
  phone: string;
  email?: string;
  accountName?: string;
  jobTitle?: string;
  notes?: string;
  contactListId?: string;
  /** Disposition tagged client-side by the view model based on the source array. */
  disposition?: string | null;
  flagged?: boolean;
  /** How many times this phone number appears across the selected lists/buckets
   *  before deduplication. Set on the kept (first) occurrence; > 1 means duplicated. */
  duplicateCount?: number;
}

export interface SessionStatusCounts {
  noAnswer: number;
  cutOff: number;
  transferError: number;
  voicemail: number;
  fuegoLead: number;
}

export interface SessionsTabState {
  selectedSessionIds: string[];
  activeFilters: SessionContactStatus[];
  newListName: string;
  searchQuery: string;
  currentPage: number;
  pageSize: number;
}

export const DEFAULT_SESSIONS_TAB_STATE: SessionsTabState = {
  selectedSessionIds: [],
  activeFilters: [],
  newListName: '',
  searchQuery: '',
  currentPage: 1,
  pageSize: 20,
};
